import React, { FC, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Layout, Row, Col } from 'antd';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { EmptyState } from '../empty_state/EmptyState';
import { getAuditFieldNameByKey } from '../../utils/AuditUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import PartEquipmentNameWithType from '../equipment_per_stock_location_row_display/PartEquipmentNameWithType';
import { UserName } from '../name_component/user/UserNameComponent';
import DateTimeHover from '../date_time_component/DateTime';
import { fetchPurchaseRequestAudits } from '../../thunks/purchase_request_audits_thunks';
import { PURCHASE_REQUEST_AUDITS_CRUD_ACTION_CREATORS } from '../../actions/purchase_request_audits_actions';
import { PURCHASE_REQUEST_STATUS_DISPLAY_NAMES } from '../../constants/PurchaseRequestStatuses';

const { Content } = Layout;

const TARGET_COLLECTION_NAME = 'purchaseRequestAuditsIndex';

const fieldMap = {
	status: 'Status',
	quantity: 'Quantity',
	receivedQuantity: 'Received Quantity',
	unitCost: 'Unit Cost',
	totalCost: 'Total Cost',
	notes: 'Notes',
	vendorId: 'Vendor',
	priority: 'Priority',
	requestedDeliveryDate: 'Requested Delivery Date',
	approvedBy: 'Approved By',
	name: 'Name',
	description: 'Description',
	internalNotes: 'Internal Notes',
	stockLocationId: 'Stock Location',
	locationId: 'Location',
	currencyId: 'Currency',
	workOrderId: 'Work Order',
	approverEmail: 'Approver Email',
	tierName: 'Tier Name',
	tierAction: 'Tier Action',
	denied: 'Denied',
	equipmentCost: 'Equipment Cost',
};

const ID_FIELD_ENTITY_MAP = {
	stockLocationId: 'stockLocation.name',
	locationId: 'location.name',
};

const resolveStatusLabel = (status) => {
	if (!status) return '--';
	return PURCHASE_REQUEST_STATUS_DISPLAY_NAMES[status] || status;
};

const formatFieldValue = (value, fallback) => {
	if (value === null || value === undefined) return fallback;
	return value;
};

const renderFieldChange = (field, record) => {
	const label = fieldMap[field.fieldName] || field.fieldName;
	const oldValue = formatFieldValue(field.oldValue, '--');

	const entityPath = ID_FIELD_ENTITY_MAP[field.fieldName];
	const resolvedNewValue = entityPath
		? formatFieldValue(nullSafeGet(entityPath, record), formatFieldValue(field.newValue, '--'))
		: formatFieldValue(field.newValue, '--');

	return (
		<span
			key={field.fieldName}
			className="inline-block rounded-md bg-violet-100 p-2 text-sm text-gray-500"
		>
			{label} : {oldValue} <ArrowRightOutlined /> {resolvedNewValue}
		</span>
	);
};

const renderStatusChange = (record) => {
	const fromStatus = nullSafeGet('fromStatus', record);
	const toStatus = nullSafeGet('toStatus', record);
	if (!fromStatus && !toStatus) return null;

	return (
		<span
			key="statusChange"
			className="inline-block rounded-md bg-violet-100 p-2 text-sm text-gray-500"
		>
			Status : {resolveStatusLabel(fromStatus)} <ArrowRightOutlined />{' '}
			{resolveStatusLabel(toStatus)}
		</span>
	);
};

const renderLineItemDetail = (record) => {
	const hasPart = nullSafeGet('part', record) || nullSafeGet('partId', record);
	const hasEquipment =
		nullSafeGet('equipmentType', record) || nullSafeGet('equipmentTypeId', record);

	if (!hasPart && !hasEquipment) return '--';

	return <PartEquipmentNameWithType record={record} backText="Back to Audits" />;
};

const PurchaseRequestAuditsTab: FC<any> = ({
	match,
	purchaseRequestAudits,
	updateFilters,
	clearAndUpdateFilters,
}) => {
	const purchaseRequestId = useMemo(() => match.params.id, [match.params.id]);

	const columns = useMemo(
		() => [
			{
				title: 'Audit Type',
				dataIndex: 'auditType',
				render: (_) => getAuditFieldNameByKey(_),
			},
			{
				title: 'Updated By',
				dataIndex: 'createdByContact',
				render: (_) => (_ ? <UserName contact={_} mode="inline" placement="top" /> : '--'),
			},
			{
				title: 'Updated At',
				dataIndex: 'createdAt',
				render: (_) => (_ ? <DateTimeHover timestamp={_} /> : '--'),
			},
			{
				title: 'Line Item',
				dataIndex: 'lineItemId',
				width: 200,
				render: (_, record) => renderLineItemDetail(record),
			},
			{
				title: 'Changes',
				dataIndex: 'fieldValueChanges',
				render: (fieldValueChanges, record) => {
					const statusChange = renderStatusChange(record);
					const fieldChanges =
						record.auditType !== 'pr_tier_approved' &&
						fieldValueChanges &&
						fieldValueChanges.length > 0
							? fieldValueChanges.map((field) => renderFieldChange(field, record))
							: [];
					const allChanges = [statusChange].concat(fieldChanges).filter(Boolean);
					return allChanges.length > 0 ? (
						<div className="flex flex-wrap gap-2">{allChanges}</div>
					) : (
						'--'
					);
				},
			},
		],
		[]
	);

	const fetchData = useCallback(
		(params, targetCollectionName, pagination, sorting, filters) => {
			return fetchPurchaseRequestAudits(
				purchaseRequestId,
				targetCollectionName,
				pagination,
				sorting,
				filters
			);
		},
		[purchaseRequestId]
	);

	return (
		<Content style={{ padding: '0 0.5em' }}>
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				<Col span={24}>
					<PaginatedReduxTableWithHeader
						targetCollectionName={TARGET_COLLECTION_NAME}
						stateSlice={purchaseRequestAudits}
						updateFilters={updateFilters}
						clearAndUpdateFilters={clearAndUpdateFilters}
						filterConfig={[]}
						rightActions={null}
						entityCollectionName="purchase_request_audits"
						tableColumns={columns}
						fetchData={fetchData}
						showHeader={true}
						hasDefaultHeaderPage={false}
						initialPagination={{ current: 1, pageSize: 10 }}
						initialSorters={{}}
						hideFilters
						hideSearch
						keyAccessor={(el) => el.id}
						emptyState={
							<EmptyState
								graphic={
									<img
										style={{ marginBottom: 8 }}
										src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
										alt="No audits found!"
									/>
								}
								headline={'No audit records found.'}
								body={null}
							/>
						}
						ignoreLocationQueryParams
					/>
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state) => ({
	purchaseRequestAudits: state.purchase_request_audits,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollectionName) =>
		dispatch(
			PURCHASE_REQUEST_AUDITS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			PURCHASE_REQUEST_AUDITS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(PurchaseRequestAuditsTab)
);
